import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Hand, FileSearch, Database, Users, FlaskConical, Eye, ArrowRight,
} from "lucide-react";
import { Reveal } from "@/components/od/Reveal";
import { MaskedLine } from "@/components/od/Hero";
import { useSimplify } from "@/components/od/simplify-context";

const GUARDRAILS = [
  {
    id: "guardrail-ask",
    icon: Hand,
    title: "It asks before it writes",
    body: "Claude reasons, drafts, and then stops. Every change to your records — a deal stage, a task, a note — waits for a human yes. The copilot proposes; people dispose.",
  },
  {
    id: "guardrail-audit",
    icon: FileSearch,
    title: "Every action is recorded",
    body: "Each approved change lands in Zoho's own audit trail, attributed to the person who approved it. You can always answer 'who changed this, and when' — because it's the same answer as always.",
  },
  {
    id: "guardrail-data",
    icon: Database,
    title: "Your data stays in your Zoho",
    body: "We don't copy your business into our systems. The copilot reads and writes inside your own Zoho CRM, Projects, and Books, under your control, on your subscriptions.",
  },
  {
    id: "guardrail-roles",
    icon: Users,
    title: "Role-based by design",
    body: "The foreman doesn't see the forecast; the estimator doesn't see payroll. Every person gets exactly what their role needs — nothing chased, nothing extra, nothing exposed.",
  },
  {
    id: "guardrail-demo",
    icon: FlaskConical,
    title: "Practise in demo mode first",
    body: "Your team learns on a safe sandbox copy before a single live write. Confidence comes from rehearsal, not from promises.",
  },
  {
    id: "guardrail-readonly",
    icon: Eye,
    title: "Read-only, if you prefer",
    body: "Start with answers only — the copilot reads and reports, and never writes. Add acting power gradually, as trust is earned.",
  },
];

export default function TrustPage() {
  const { setOpen } = useSimplify();

  useEffect(() => {
    document.title = "Guardrails in Plain Language — Trust & Safety | Ocean Data";
  }, []);

  return (
    <main className="pt-16 sm:pt-20 bg-[#F8FAFC]">
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-6">
            Trust & guardrails
          </p>
          <h1
            data-testid="trust-headline"
            className="font-display font-bold tracking-tight leading-[1.04] text-brand-navy text-4xl sm:text-5xl lg:text-6xl max-w-3xl"
          >
            <MaskedLine delay={0.15} testId="trust-headline-line-1">Powerful underneath.</MaskedLine>
            <MaskedLine delay={0.3} testId="trust-headline-line-2">
              <span className="text-brand-teal">Polite on the surface.</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
          >
            Letting an AI near your business records is a fair thing to be
            careful about. Here is exactly how ours behaves — in plain
            language, no fine print.
          </motion.p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-7 mt-16">
            {GUARDRAILS.map((g, i) => (
              <Reveal key={g.id} delay={i * 0.06} className="h-full">
                <div
                  data-testid={g.id}
                  className="h-full rounded-2xl bg-white border border-slate-200 p-7 sm:p-8 transition-all duration-300 hover:border-brand-teal hover:-translate-y-0.5"
                >
                  <span className="w-11 h-11 rounded-full bg-brand-tealsoft text-brand-teal flex items-center justify-center mb-6">
                    <g.icon size={18} />
                  </span>
                  <h3 className="font-display font-semibold text-lg text-brand-navy leading-snug mb-3">
                    {g.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{g.body}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-14 rounded-2xl bg-brand-navy text-white px-7 sm:px-10 py-9 flex flex-col lg:flex-row lg:items-center gap-6 justify-between">
              <div>
                <p className="font-display font-semibold text-xl leading-snug mb-2">
                  Still cautious? Good. So are we.
                </p>
                <p className="text-sm text-slate-300 leading-relaxed max-w-xl">
                  Watch the copilot behave on sample data first —{" "}
                  <Link to="/demos" className="font-semibold text-brand-gold hover:text-white transition-colors">
                    the live demos
                  </Link>{" "}
                  show the same guardrails you'll get. Or read{" "}
                  <Link to="/about" className="font-semibold text-brand-gold hover:text-white transition-colors">
                    how Bruno works
                  </Link>
                  .
                </p>
              </div>
              <button
                data-testid="trust-cta-button"
                onClick={() => setOpen(true)}
                className="shrink-0 inline-flex items-center gap-2.5 rounded-full bg-brand-teal text-white text-sm font-semibold px-8 py-4 transition-all duration-300 hover:bg-brand-tealhover hover:-translate-y-0.5"
              >
                Ask us anything
                <ArrowRight size={16} />
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
